import { Component, OnInit, OnDestroy, PLATFORM_ID, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from './../../../environments/environment';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { MarketingConfirmationComponent } from 'src/app/modals/marketing-confirmation/marketing-confirmation.component';
import {
  User,
  UserTelephoneType,
  ProductType,
  CommunicationPreferencesSettings,
} from 'src/app/interfaces/user';
import { Alert } from 'src/app/interfaces/alert';
import { UserService } from 'src/app/services/user/user.service';
import { ActivatedRoute, Router } from '@angular/router';
import { UpdateDetailsModalComponent } from 'src/app/modals/update-details-modal/update-details-modal.component';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { UpdatedAfterThirdPartyCommunicationUserState } from 'src/app/constants/app.constants';
import { HelperService } from 'src/app/shared/helpers/helper.service';
import { CardDetail } from 'src/app/interfaces/Account/cardDetail';
import { isPlatformBrowser } from '@angular/common';
import { ClickEventNumbersEnum, TrackingPayloadType } from 'src/app/interfaces/clickEventNumbers';
import { ClickTrackerService } from 'src/app/services/click-tracker/click-tracker.service';
import { ConfigService } from 'src/app/services/config/config.service';
import { FeatureFlagsService } from 'src/app/services/feature-flag/feature-flag.service';

export enum ThirdPartyCommunicationTypes {
  thirdPartyCommunicationEmail = 'thirdPartyCommunicationEmail',
  thirdPartyCommunicationSms = 'thirdPartyCommunicationSms',
  inverseThirdPartyCommunicationEmail = 'inverseThirdPartyCommunicationEmail',
  inverseThirdPartyCommunicationSms = 'inverseThirdPartyCommunicationSms',
}

@Component({
  selector: 'app-account',
  templateUrl: './account.component.html',
  styleUrls: ['./account.component.scss'],
})
export class AccountComponent implements OnInit, OnDestroy {
  loading = false;
  alert: Alert = null;
  user: User;
  communicationPreferences: CommunicationPreferencesSettings;
  userTelephoneType = UserTelephoneType;
  productType = ProductType;
  marketingConfirmationModalRef?: NgbModalRef;
  updateDetailsModalRef?: NgbModalRef;
  upgradeNoPetAlert = false;
  showCardExpirationBox = false;
  updateCardLink = '';
  isExpiringThisMonth = false;
  basePlusCanAddAndEditSecoundAddress: boolean;
  isBrowser: boolean;
  trackingPayload: TrackingPayloadType = {
    event: ClickEventNumbersEnum.AccountDetails,
    details: ''
  };
  useRebrandedLayout: boolean;

  private _activateThirdPartyCommunicationStep = false;
  private _thirdpartycommunication = 'thirdpartycommunication';
  public plan1Name: string;
  public plan2Name: string;
  public plan3Name: string;

  constructor(
    public userService: UserService,
    private modalService: NgbModal,
    private route: ActivatedRoute,
    private _helperService: HelperService,
    private http: HttpClient,
    private _router: Router,
    private clickTrackerService: ClickTrackerService,
    private configService: ConfigService,
    private featureFlagService: FeatureFlagsService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.user = this.route.snapshot.data.user as User;
    this.communicationPreferences = {
      emailChecked: !this.user.communicationPreferences.email,
      smsChecked: !this.user.communicationPreferences.sms,
      telephoneChecked: !this.user.communicationPreferences.telephone,
    };
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.plan1Name = this.configService.getTextFromKey('1-plan-name');
    this.plan2Name = this.configService.getTextFromKey('2-plan-name');
    this.plan3Name = this.configService.getTextFromKey('3-plan-name');
    this.useRebrandedLayout = this.featureFlagService.isEnabled('useRebrandedLayout');
    this.basePlusCanAddAndEditSecoundAddress = this.featureFlagService.isEnabled('basePlusCanAddAndEditSecoundAddress');
  }

  public openReverseModal(property: string, event: MatCheckboxChange, isThirdParty = false) {
    if (!event.checked) {
      this.trackEvent(`accountdash::${property}consent::switchedoff`);
      if (isThirdParty) this.handleInverseOfThirdPartyCommunication(property);
      return;
    }

    this.marketingConfirmationModalRef = this.modalService.open(MarketingConfirmationComponent, {
      backdrop: 'static',
      centered: true,
      size: 'md',
    });
    this.marketingConfirmationModalRef.result
      .then(() => {
        if (isThirdParty) {
          this.user.communicationPreferences[property] = false;
          this.handleInverseOfThirdPartyCommunication(property);
        } else {
          this.communicationPreferences[property] = false;
        }
      })
      .catch(() => {
        if (isThirdParty) {
          this.user.communicationPreferences[property] = true;
          this.handleInverseOfThirdPartyCommunication(property);
        } else {
          this.communicationPreferences[property] = true;
        }
        this.trackEvent(`accountdash::${property}consent::switchedon`);
      });
  }

  openModal(property: string, event: MatCheckboxChange) {
    if (event.checked) {
      this.trackEvent(`accountdash::${property}consent::switchedon`);
      this.handleInverseOfThirdPartyCommunication(property);
      return;
    }

    this.marketingConfirmationModalRef = this.modalService.open(MarketingConfirmationComponent, {
      backdrop: 'static',
      centered: true,
      size: 'md',
    });
    this.marketingConfirmationModalRef.result
      .then(() => {
        this.user.communicationPreferences[property] = true;
        this.handleInverseOfThirdPartyCommunication(property);
      })
      .catch(() => {
        this.trackEvent(`accountdash::${property}consent::switchedoff`);
        this.handleInverseOfThirdPartyCommunication(property);
      });
  }

  openUpdateDetailsModal(): void {
    const modalOptions: NgbModalOptions = {
      backdrop: 'static',
      centered: true,
      keyboard: false,
      size: 'lg',
    };

    this.updateDetailsModalRef = this.modalService.open(UpdateDetailsModalComponent, modalOptions);
    this.updateDetailsModalRef.componentInstance.user = this.user;
    this.updateDetailsModalRef.result
      .then(() => {})
      .catch((reason) => {
        if (reason === 'error') {
          this.alert = {
            type: 'danger',
            message: 'An error has occurred. Please try again later.',
          };
        } else if (reason === 'timeout') {
          this.alert = {
            type: 'danger',
            message:
              'The payment verification has timed out. Please visit My Invoices from the menu, here you will find an invoice to complete the payment and then you will be able to make your changes. If payment has been made, please reload this page.',
          };
        }
      });
  }

  save() {
    this.closeAlert();
    this.loading = true;
    const userChanges: Partial<User> = {
      communicationPreferences: {
        email: !this.communicationPreferences.emailChecked,
        sms: !this.communicationPreferences.smsChecked,
        telephone: !this.communicationPreferences.telephoneChecked,

        thirdPartyCommunicationEmail:
          this.user.communicationPreferences.thirdPartyCommunicationEmail,
        thirdPartyCommunicationSms: this.user.communicationPreferences.thirdPartyCommunicationSms,
        inverseThirdPartyCommunicationEmail:
          !this.user.communicationPreferences.thirdPartyCommunicationEmail,
        inverseThirdPartyCommunicationSms:
          !this.user.communicationPreferences.thirdPartyCommunicationSms,
      },
      updatedAfterThirdPartyCommAdd:
        this.user.updatedAfterThirdPartyCommAdd ==
        UpdatedAfterThirdPartyCommunicationUserState.NotUpdated
          ? UpdatedAfterThirdPartyCommunicationUserState.Updated
          : UpdatedAfterThirdPartyCommunicationUserState.NotAffected,
    };

    this.userService
      .patchMe(userChanges)
      .subscribe(
        (updatedUser) => {
          this.user = updatedUser;
          this.initInverseThirdPartyCommunications();

          if (this._activateThirdPartyCommunicationStep) {
            this.alert = {
              type: 'success',
              message:
                "Your account information and third party communication was updated. You'll get redirected in a few seconds!",
            };
            setTimeout(() => {
              this._router.navigate(['/dashboard']);
            }, 1000);
            return;
          }

          this.alert = {
            type: 'success',
            message: 'Your account information has been saved successfully.',
          };
        },
        (err) => {
          this.alert = {
            type: 'danger',
            message: 'An error has occurred. Please try again later.',
          };
        }
      )
      .add(() => {
        this.loading = false;
      });
  }

  closeAlert() {
    this.alert = null;
    this.upgradeNoPetAlert = false;
  }

  trackEvent(event: string) {
    if (!this.isBrowser) return;
    window.analytics.track(event);
  }

  ngOnInit(): void {
    this.initInverseThirdPartyCommunications();
    this.adjustThirdPartyCommunicationStates();
    this.checkQueryParams();
    this.checkCardExpiration();
    this.getCardUpdateLink();
    if (this.isBrowser) {
      this.clickTrackerService.sendPayload(this.trackingPayload);
    }
  }

  public getDateOfBirth() {
    if (this.user.dateOfBirth) {
      const day = new Date(this.user.dateOfBirth).getDate();
      const month = new Date(this.user.dateOfBirth).getMonth() + 1;
      const year = new Date(this.user.dateOfBirth).getFullYear();
      return `${day}/${month}/${year}`;
    }

    return '-';
  }

  ngOnDestroy(): void {
    this.marketingConfirmationModalRef?.dismiss('destroyed');
    this.updateDetailsModalRef?.dismiss('destroyed');
  }

  private initInverseThirdPartyCommunications() {
    this.user.communicationPreferences.inverseThirdPartyCommunicationEmail =
      !this.user.communicationPreferences.thirdPartyCommunicationEmail;
    this.user.communicationPreferences.inverseThirdPartyCommunicationSms =
      !this.user.communicationPreferences.thirdPartyCommunicationSms;
  }

  private handleInverseOfThirdPartyCommunication(propertyName: string) {
    if (propertyName.toLowerCase().includes(this._thirdpartycommunication)) {
      switch (propertyName) {
        case ThirdPartyCommunicationTypes.thirdPartyCommunicationEmail:
          this.user.communicationPreferences.inverseThirdPartyCommunicationEmail =
            !this.user.communicationPreferences.thirdPartyCommunicationEmail;
          break;
        case ThirdPartyCommunicationTypes.thirdPartyCommunicationSms:
          this.user.communicationPreferences.inverseThirdPartyCommunicationSms =
            !this.user.communicationPreferences.thirdPartyCommunicationSms;
          break;
        case ThirdPartyCommunicationTypes.inverseThirdPartyCommunicationEmail:
          this.user.communicationPreferences.thirdPartyCommunicationEmail =
            !this.user.communicationPreferences.inverseThirdPartyCommunicationEmail;
          break;
        case ThirdPartyCommunicationTypes.inverseThirdPartyCommunicationSms:
          this.user.communicationPreferences.thirdPartyCommunicationSms =
            !this.user.communicationPreferences.inverseThirdPartyCommunicationSms;
          break;
      }
    }
  }

  public getCardUpdateLink() {
    this.userService.getUserCardUpdateLink().subscribe((data: string) => {
      this.updateCardLink = data;
    });
  }

  private checkQueryParams() {
    if (
      this.route.snapshot.queryParams.activateThirdPartyCommunicationStep &&
      this.route.snapshot.queryParams.activateThirdPartyCommunicationStep === 'true'
    ) {
      this._activateThirdPartyCommunicationStep = true;
      this._helperService.scrollToElementById('communicationPreferencesSection');
    }
  }

  private adjustThirdPartyCommunicationStates() {
    if (
      this.user.updatedAfterThirdPartyCommAdd ==
      UpdatedAfterThirdPartyCommunicationUserState.NotUpdated
    ) {
      if (this.user.communicationPreferences.email) {
        this.user.communicationPreferences.thirdPartyCommunicationEmail = true;
        this.user.communicationPreferences.inverseThirdPartyCommunicationEmail = false;
      }

      if (this.user.communicationPreferences.sms) {
        this.user.communicationPreferences.thirdPartyCommunicationSms = true;
        this.user.communicationPreferences.inverseThirdPartyCommunicationSms = false;
      }
    }
  }

  private checkCardExpiration() {
    this.userService.getUserCards().subscribe((data: CardDetail[]) => {
      if (data !== null) {
        data.forEach((card: CardDetail) => {
          this.adjustCardExpirationBoxState(card);
        });
      }
    });
  }

  private adjustCardExpirationBoxState(card: CardDetail) {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1; // Months are 0-based in JavaScript, so we add 1

    const cardExpiryYear = card.ExpiryYear;
    const cardExpiryMonth = card.ExpiryMonth;
    //TODO remove this two variable after the test.
    const isUserForExpiryTest = this.user.contact.email && this.user.contact.email === 'mihaly.sebesi+url@cognitivecreators.com'
    const isUserForExpiredTest = this.user.contact.email && this.user.contact.email === 'mihaly.sebesi@cognitivecreators.com'

    let isExpired = false;

    // Check if the card is expired
    if (
      cardExpiryYear < currentYear ||
      (cardExpiryYear === currentYear && cardExpiryMonth < currentMonth)
    ) {
      isExpired = true;
    }

    // Check if the card is expiring this month
    if ((cardExpiryYear === currentYear && cardExpiryMonth === currentMonth) || isUserForExpiryTest) {
      this.isExpiringThisMonth = true;
    }

    // Show the card expiration box if the card is expired or expiring this month
    if (isExpired || this.isExpiringThisMonth || isUserForExpiryTest || isUserForExpiredTest) {
      this.showCardExpirationBox = true;
    }
  }
}
